import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageLayout, PageBody, BookNowButton } from "@/components/site/PageLayout";
import { PHONE_DISPLAY, PHONE_TEL, SERVICES } from "@/data/services";

const EMPTY = {
  name: "", phone: "", email: "", contactMethod: "Call", pkg: "Not Sure",
  paintCorrection: "No", ceramic: "No", date: "", notes: "", company: "",
};

export default function SchedulePage() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", {
        name: form.name,
        phone: form.phone,
        email: form.email,
        vehicle: "",
        service: form.pkg,
        notes: [
          `Preferred contact: ${form.contactMethod}`,
          `Paint correction: ${form.paintCorrection}`,
          `Polishing: ${form.ceramic}`,
          form.date ? `Preferred date: ${form.date}` : "",
          form.notes,
        ].filter(Boolean).join(" · "),
        company: form.company,
      });
      setSent(true);
      toast.success("Quote request sent! We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const Radio = ({ name, value, options, testid }) => (
    <div data-testid={testid} className="flex gap-3">
      {options.map((o) => (
        <button
          type="button"
          key={o}
          data-testid={`${testid}-${o.toLowerCase()}`}
          onClick={() => setForm((f) => ({ ...f, [name]: o }))}
          className={`px-6 py-3 font-display uppercase text-xs tracking-[0.12em] transition-colors border ${
            value === o ? "bg-[#EA0A0B] border-[#EA0A0B] text-white" : "border-[#232329] text-slate-300 hover:border-[#EA0A0B]"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );

  return (
    <PageLayout eyebrow="Schedule Your Service" title="Get a" titleAccent="quote">
      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            {sent ? (
              <div data-testid="schedule-success" className="bg-[#121216] border border-[#232329] p-10 text-center">
                <h2 className="font-display uppercase text-2xl tracking-wide text-white">Request received</h2>
                <p className="mt-3 text-sm text-slate-400">
                  Thanks {form.name.split(" ")[0] || "friend"} — we'll reach out via your preferred
                  contact method shortly.
                </p>
                <button
                  data-testid="schedule-send-another"
                  onClick={() => { setForm(EMPTY); setSent(false); }}
                  className="mt-6 jpd-link-underline font-display uppercase text-xs tracking-[0.15em] text-[#FF4311]"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form data-testid="schedule-form" onSubmit={submit} className="bg-[#121216] border border-[#232329] p-7 md:p-9 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="schedule-name-input" required value={form.name} onChange={set("name")} placeholder="Full name *" className="jpd-input" />
                  <input data-testid="schedule-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="Phone *" className="jpd-input" />
                </div>
                <input data-testid="schedule-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className="jpd-input" />

                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Preferred contact method</p>
                  <Radio name="contactMethod" value={form.contactMethod} options={["Call", "Text"]} testid="schedule-contact-method" />
                </div>

                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Package you would like</p>
                  <select data-testid="schedule-package-select" value={form.pkg} onChange={set("pkg")} className="jpd-input">
                    <option>Not Sure</option>
                    {SERVICES.map((s) => (
                      <option key={s.id}>{s.name.charAt(0) + s.name.slice(1).toLowerCase().replace("detail package", "Detail Package")}</option>
                    ))}
                  </select>
                  <p className="mt-2 text-xs text-slate-500">
                    Go to our packages page to{" "}
                    <Link to="/packages" className="text-[#FF4311] hover:underline">learn more</Link> →
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    We can also detail your{" "}
                    <Link to="/boat-detailing-packages" className="text-[#FF4311] hover:underline">Boat</Link>,{" "}
                    <Link to="/motorcycle-detailing-packages" className="text-[#FF4311] hover:underline">Motorcycle</Link> or{" "}
                    <Link to="/rv-detailing-packages" className="text-[#FF4311] hover:underline">RV</Link>
                  </p>
                </div>

                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Would you like the Paint Correction Package?</p>
                  <Radio name="paintCorrection" value={form.paintCorrection} options={["Yes", "No"]} testid="schedule-paint-correction" />
                </div>

                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Would you like to add Polishing?</p>
                  <Radio name="ceramic" value={form.ceramic} options={["Yes", "No"]} testid="schedule-ceramic" />
                </div>

                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Do you have a preferred date for service?</p>
                  <input data-testid="schedule-date-input" type="date" value={form.date} onChange={set("date")}
                    min={new Date().toISOString().slice(0, 10)} className="jpd-input [color-scheme:dark]" />
                </div>

                <textarea data-testid="schedule-notes-input" value={form.notes} onChange={set("notes")} placeholder="Anything else we should know?" rows={3} className="jpd-input resize-none" />
                <input type="text" name="company" value={form.company} onChange={set("company")} tabIndex={-1} autoComplete="off" className="jpd-honeypot" aria-hidden="true" />

                <button
                  data-testid="schedule-submit-button"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#EA0A0B] hover:bg-[#C20A0B] disabled:opacity-60 text-white font-display uppercase tracking-[0.12em] text-sm py-4 transition-colors"
                >
                  {loading ? "Sending…" : "Request My Quote"} <ArrowUpRight className="w-4 h-4" />
                </button>

                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Want to skip the quote and book directly? Use our online booking.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <BookNowButton to="/book" label="Book Online Now" testid="schedule-book-button" />
                  </div>
                </div>
              </form>
            )}
          </div>

          <aside className="space-y-6">
            <div className="bg-[#121216] border border-[#232329] p-6">
              <h3 className="font-display uppercase text-sm tracking-[0.15em] text-[#FF4311] flex items-center gap-2"><Phone className="w-4 h-4" /> Call Us</h3>
              <a href={PHONE_TEL} data-testid="schedule-phone-link" className="mt-3 block text-lg font-semibold text-white hover:text-[#FF4311] transition-colors">{PHONE_DISPLAY}</a>
            </div>
            <div className="bg-[#121216] border border-[#232329] p-6">
              <h3 className="font-display uppercase text-sm tracking-[0.15em] text-[#FF4311] flex items-center gap-2"><MapPin className="w-4 h-4" /> Service Area</h3>
              <p className="mt-3 text-sm text-slate-300">Charlotte, NC and Surroundings</p>
            </div>
            <div className="bg-[#121216] border border-[#232329] p-6">
              <h3 className="font-display uppercase text-sm tracking-[0.15em] text-[#FF4311] flex items-center gap-2"><Clock className="w-4 h-4" /> Hours</h3>
              <p className="mt-3 text-sm text-slate-300">
                Call, Text {PHONE_DISPLAY} or Email us to setup your appointment. Appointments
                Monday through Saturday.
              </p>
            </div>
          </aside>
        </div>
      </PageBody>
    </PageLayout>
  );
}
